import React, { PureComponent, useEffect } from 'react';

import { ConfirmationModal } from '@stratumn/atomic';

import * as Sentry from '@sentry/react';
import compose from 'lodash.flowright';
// deep equality
import isequal from 'lodash.isequal';
import { AnimatePresence } from 'motion/react';
import PropTypes from 'prop-types';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeGrid as Grid } from 'react-window';

import { cn } from '@/shadcn/lib/utils';

import { TableRowDiffViewer } from '@/components/monaco/diffViewer/dialog/tableRow.diffViewer';
import { notify } from '@/components/toast';
import TraceIconSpinner from '@/components/ui/traceIconSpinner';
import { withWorkflowOverviewContext } from '@/components/workflowOverview/context.workflowOverview';
// table components
import { localStorageKey } from '@/components/workflowOverview/utils';
import { deepGet } from '@/utils';
import { exportDataTable } from '@/utils/export';
import { getByPath } from '@/utils/widgets';
import { withWorkflowContext } from '@/utils/workflowContext';
import { AggregationRow } from './aggregationRow';
import { ColHeaders } from './colHeaders/colHeaders';
import { ColHeadersEditor } from './colHeadersEditor/colHeadersEditor';
import {
  cleanupUserDisplayConfig,
  getCompletedDisplayConfig,
  getDataToDisplay,
  getUsedDisplayConfig,
  reorder,
  SCROLLBAR_SIZE
} from './displayConfigUtils';
import ResizableRow from './resizableRow';
import { computeNextSelection } from './selection.utils';
import { TableCell } from './tableCell/tableCell';
import { TableContext } from './tableContext';
import { TableFilters } from './tableFilters/tableFilters';
import TableHeaders from './tableHeaders';

const NB_ROWS_IF_EMPTY = 100;

const DEFAULT_EXPORT_CSV = {
  delimiter: ';',
  filename: 'table_export'
};

const CSS_BOX_SHADOW = 'box-shadow';
const CSS_BORDER_RIGHT = 'border-right';

const DEFAULT_OVERSCANROWCOUNT = 10;
const FLOATING_GRID_MIN_RATIO = 1 / 3;
const SHRINK_HEADER_ICON_THRESHOLD = 1100;
const FIXED_GRID_VERTICAL_SHADOW = '15px 0px 15px -5px rgba(75, 53, 210, 0.1)';
const GRID_HEADERS_HORIZONTAL_SHADOW =
  '0px 15px 15px -5px rgba(75, 53, 210, 0.1)';

// ToggleAwareGrid below is a small HOC tool that is able to ask a react-window grid to recompute its columns layout
// using a ref to it, based on the changes of a toggle prop
// it is used by the floating columns grid of the Table to trigger recomputation of the columns layout
// when the width is such that the layout changes between fixed/float grid to a merged grid
// this is needed because there is no equivalent in classes of useEffect on variables computed in render methods
// that are neither props or state (and impact just a small part of the rendering)
// could have used a ref on the toggle as well but didn't like the idea of moving out of react pattern
// when rewriting the Table component as a function we'll be able to handle this in a simple useEffect
const ToggleAwareGridDriver = React.memo(({ toggle, gridRef, children }) => {
  useEffect(() => {
    if (gridRef.current) {
      // recompute columns only
      gridRef.current.resetAfterColumnIndex(0);
    }
  }, [toggle]);
  return children;
});
ToggleAwareGridDriver.propTypes = {
  toggle: PropTypes.bool.isRequired,
  gridRef: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
};

const isSelectionControlled = props =>
  Array.isArray(props.update?.initialSelectedRows);

// table component itself
export class Table extends PureComponent {
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    config: PropTypes.object.isRequired,
    errorSchema: PropTypes.object,
    update: PropTypes.shape({
      loading: PropTypes.bool,
      disableClientSideSortFilter: PropTypes.bool,
      onClickConfig: PropTypes.func,
      configClicked: PropTypes.bool,
      onClickNew: PropTypes.func,
      newClicked: PropTypes.bool,
      onClickArchive: PropTypes.func,
      archiveClicked: PropTypes.bool,
      onClickUpdate: PropTypes.func,
      updateClicked: PropTypes.bool,
      setSelectedRows: PropTypes.func,
      initialSelectedRows: PropTypes.arrayOf(PropTypes.string),
      userDisplay: PropTypes.shape({
        onUpdate: PropTypes.func,
        localStorageKey: PropTypes.string,
        config: PropTypes.object
      }),
      edit: PropTypes.shape({
        modifiedData: PropTypes.object,
        onDataModified: PropTypes.func,
        allowDisplayDiffs: PropTypes.bool,
        onClickBatchEdit: PropTypes.func,
        batchEditClicked: PropTypes.bool,
        onDeleteSelectedRows: PropTypes.func
      }),
      pagination: PropTypes.shape({
        buffer: PropTypes.number.isRequired,
        hasNextPage: PropTypes.bool.isRequired,
        onLoadMore: PropTypes.func.isRequired
      }),
      group: PropTypes.shape({
        indexPath: PropTypes.string,
        rowsPath: PropTypes.string,
        rowCountPath: PropTypes.string,
        onLoadMore: PropTypes.func
      }),
      exportSetup: PropTypes.shape({
        getConfirmationSetup: PropTypes.func,
        generateExportPayload: PropTypes.func
      })
    }),
    headerConfig: PropTypes.shape({
      isCompact: PropTypes.bool,
      newLabel: PropTypes.string,
      fastOverview: PropTypes.bool
    }),
    workflowContext: PropTypes.object,
    context: PropTypes.shape({
      savedViewsArray: PropTypes.arrayOf(PropTypes.object),
      updateSavedViewsArray: PropTypes.func,
      userDisplayConfig: PropTypes.object
    }),
    className: PropTypes.string
  };

  static defaultProps = {
    update: {},
    workflowContext: null,
    context: {
      savedViewsArray: null,
      setsavedViewsArray: () => {}
    }
  };
  dataSelectorPath;

  constructor(props) {
    super(props);

    const { data, config } = props;
    /**
     * @type {import('@/components/ui/table/table.type').TableUpdate}
     */
    const update = props.update || {};

    // grid ref, kept for resizing and scrolling events
    this.colHeadersRef = React.createRef();

    this.fixedColHeadersRef = React.createRef();
    this.fixedGridRef = React.createRef();
    this.fixedGridOuterRef = React.createRef();
    this.fixedAggregationRowRef = React.createRef();

    this.floatingColHeadersRef = React.createRef();
    this.floatingGridRef = React.createRef();
    this.floatingGridOuterRef = React.createRef();
    this.floatingAggregationRowRef = React.createRef();

    this.dataSelectorPath = config.dataSelectorPath || 'rowId';

    // this is a ref to keep track of whether the grid merging status has changed
    // and trigger floatingGrid ref actualisation
    this.mergeGrids = React.createRef(false);

    let userDisplayConfig = deepGet(update, 'userDisplay.config', null);
    const key = deepGet(update, 'userDisplay.localStorageKey');
    if (!userDisplayConfig && key) {
      // get the user display config document from local storage
      const localStorageDisplayConfigStr = localStorage.getItem(key);
      userDisplayConfig = localStorageDisplayConfigStr
        ? JSON.parse(localStorageDisplayConfigStr)
        : null;
    }

    // complete the display config with all required widths etc...
    const displayConfig = getCompletedDisplayConfig(config, userDisplayConfig);

    // initial sort and filter of the data to display if necessary
    const dataToDisplay = update?.disableClientSideSortFilter
      ? data
      : getDataToDisplay(data, displayConfig);

    this.state = {
      userDisplayConfig, // this is the raw display config read from the specs or local storage
      displayConfig,
      selectedData: update.initialSelectedRows || [],
      lastSelectedRowIndex: null,
      isEditing: false,
      dataToDisplay,
      displayDiffs: false,
      exporting: false,
      exportConfirmation: null,
      fileType: 'csv',
      showTracesDiffViewer: false
    };
  }

  componentDidUpdate(prevProps) {
    if (!isSelectionControlled(this.props)) {
      return;
    }

    const nextInitial = this.props.update.initialSelectedRows;
    const dataChanged = prevProps.data !== this.props.data;
    if (
      !dataChanged &&
      isequal(prevProps.update?.initialSelectedRows, nextInitial)
    ) {
      return;
    }

    const availableIds = new Set(
      (this.props.data || []).map(row =>
        String(getByPath(row, this.dataSelectorPath))
      )
    );
    const selectedData = nextInitial.filter(id => availableIds.has(String(id)));

    if (dataChanged || !isequal(selectedData, this.state.selectedData)) {
      this.setState({
        selectedData,
        ...(dataChanged && { lastSelectedRowIndex: null })
      });
    }

    // Rows can leave `data` while still selected (delete_rows); the owner of the
    // selection has to drop them too, or batch edits target rows that are gone.
    if (!isequal(selectedData, nextInitial)) {
      this.props.update.setSelectedRows?.(selectedData);
    }
  }

  componentDidMount() {
    const { userDisplayConfig } = this.props.context;

    this.updateUserDisplayConfig(userDisplayConfig);
  }

  saveConfig = name => {
    const { workflowContext } = this.props;
    const { savedViewsArray, updateSavedViewsArray } = this.props.context;

    const config = JSON.parse(
      localStorage.getItem(localStorageKey(workflowContext.rowId))
    );
    const newView = {
      id: Date.now(),
      name,
      config
    };
    const updatedConfigsArray = [...savedViewsArray, newView];
    updateSavedViewsArray(updatedConfigsArray);
  };

  updateGrid = () => {
    // tell the Grid components to update its cache
    if (this.fixedGridRef.current) {
      this.fixedGridRef.current.resetAfterColumnIndex(0);
      this.fixedGridRef.current.resetAfterRowIndex(0);
    }
    if (this.floatingGridRef.current) {
      this.floatingGridRef.current.resetAfterColumnIndex(0);
      this.floatingGridRef.current.resetAfterRowIndex(0);
    }
  };

  onFixedGridScroll = ({ scrollTop }) => {
    if (
      this.floatingGridRef.current &&
      this.floatingGridOuterRef.current &&
      this.floatingGridOuterRef.current.scrollTop !== scrollTop
    ) {
      this.floatingGridRef.current.scrollTo({
        scrollTop
      });
    }
  };

  onFloatingGridScroll = ({ scrollLeft, scrollTop }) => {
    // check if we need to scroll floating col headers horizontally
    if (
      this.floatingColHeadersRef.current &&
      this.floatingColHeadersRef.current.scrollLeft !== scrollLeft
    ) {
      this.floatingColHeadersRef.current.scrollLeft =
        this.floatingGridOuterRef.current.scrollLeft;
    }
    // check if we need to scroll floating aggregation row horizontally
    if (
      this.floatingAggregationRowRef.current &&
      this.floatingAggregationRowRef.current.scrollLeft !== scrollLeft
    ) {
      this.floatingAggregationRowRef.current.scrollLeft =
        this.floatingGridOuterRef.current.scrollLeft;
    }
    // check if we need to scroll fixed grid vertically
    if (
      this.fixedGridRef.current &&
      this.fixedGridOuterRef.current &&
      this.fixedGridOuterRef.current.scrollTop !== scrollTop
    ) {
      this.fixedGridRef.current.scrollTo({
        scrollTop
      });
    }
    // check if we need to apply box shadow to col headers bottom border
    if (this.colHeadersRef.current) {
      if (scrollTop === 0) {
        this.colHeadersRef.current.style.removeProperty(CSS_BOX_SHADOW);
      } else {
        this.colHeadersRef.current.style.setProperty(
          CSS_BOX_SHADOW,
          GRID_HEADERS_HORIZONTAL_SHADOW
        );
      }
    }
    // check if we need to apply box shadow to fixed col headers and grid right border
    if (this.fixedColHeadersRef.current && this.fixedGridOuterRef.current) {
      if (scrollLeft === 0) {
        this.fixedColHeadersRef.current.style.removeProperty(CSS_BOX_SHADOW);
        this.fixedGridOuterRef.current.style.removeProperty(CSS_BOX_SHADOW);
        this.fixedGridOuterRef.current.style.removeProperty(CSS_BORDER_RIGHT);
        if (this.fixedAggregationRowRef.current) {
          this.fixedAggregationRowRef.current.style.removeProperty(
            CSS_BOX_SHADOW
          );
        }
      } else {
        this.fixedColHeadersRef.current.style.setProperty(
          CSS_BOX_SHADOW,
          FIXED_GRID_VERTICAL_SHADOW
        );
        this.fixedGridOuterRef.current.style.setProperty(
          CSS_BOX_SHADOW,
          FIXED_GRID_VERTICAL_SHADOW
        );
        if (this.fixedAggregationRowRef.current) {
          this.fixedAggregationRowRef.current.style.setProperty(
            CSS_BOX_SHADOW,
            FIXED_GRID_VERTICAL_SHADOW
          );
        }
      }
    }
  };

  // scroll the floating grid left if the floating col headers scroll (happens with col headers drag n drop)
  onFloatingColHeadersScroll = () => {
    if (
      this.floatingGridRef.current &&
      this.floatingGridOuterRef.current &&
      this.floatingGridOuterRef.current.scrollLeft !==
        this.floatingColHeadersRef.current.scrollLeft
    ) {
      this.floatingGridRef.current.scrollTo({
        scrollLeft: this.floatingColHeadersRef.current.scrollLeft
      });
    }
  };

  UNSAFE_componentWillUpdate = nextProps => {
    const newUserDisplayConfig =
      nextProps.update &&
      (!this.props.update ||
        nextProps.update.userDisplay !== this.props.update.userDisplay);
    if (
      nextProps.config !== this.props.config ||
      nextProps.data !== this.props.data ||
      newUserDisplayConfig
    ) {
      if (
        nextProps.data !== this.props.data &&
        !isSelectionControlled(nextProps)
      ) {
        // Controlled selection is reconciled by componentDidUpdate instead.
        this.setState({ selectedData: [], lastSelectedRowIndex: null });
      }
      const userDisplayConfig = deepGet(nextProps, 'update.userDisplay.config');
      this.regenerateCompletedDisplayConfig(
        nextProps,
        userDisplayConfig !== undefined
          ? userDisplayConfig
          : this.state.userDisplayConfig,
        true
      );
    }
  };

  getFixedColumnWidth = idx => {
    const { selectBoxWidth, fixedColumns } = this.state.displayConfig;
    // fixed columns grid holds selectBox at idx=0
    return idx === 0 ? selectBoxWidth : fixedColumns[idx - 1].width;
  };

  getFloatingColumnWidth = idx => {
    const { columns } = this.state.displayConfig;
    return columns[idx].width;
  };

  getMergedColumnsWidth = idx => {
    const { fixedColumns, columns } = this.state.displayConfig;
    const mergedColumns = [...fixedColumns, ...columns];
    return mergedColumns[idx].width;
  };

  getEstimatedFloatingColumnWidth = () => {
    const { columns } = this.state.displayConfig;
    const totalWidth = columns.reduce(
      (width, column) => width + column.width,
      0.0
    );
    return totalWidth / columns.length;
  };

  getEstimatedMergedColumnWidth = () => {
    const { fixedColumns, columns } = this.state.displayConfig;
    const mergedColumns = [...fixedColumns, ...columns];
    const totalWidth = mergedColumns.reduce(
      (width, column) => width + column.width,
      0.0
    );
    return totalWidth / mergedColumns.length;
  };

  getRowHeight = () =>
    // if the table is asked to display diffs vs patches, double the rows height
    (this.state.displayDiffs ? 2 : 1) * this.state.displayConfig.rowsHeight;

  // returns a copy / sanitized version of the current user display config (the one cached in localStorage)
  getCurrentDisplayConfig = () =>
    getUsedDisplayConfig(this.props.config, this.state.userDisplayConfig);

  updateUserDisplayConfig = newUserDisplayConfig => {
    const { config, update } = this.props;
    const { userDisplay = {} } = update || {};

    // first cleanup a bit the new config, ie remove empty things
    // this is required to reduce the user config
    // and have a better sense of when it is not empty (and show the reset button)
    const usedDisplayConfig = cleanupUserDisplayConfig(
      config,
      newUserDisplayConfig
    );

    // If a user display config handler has been passed, use it
    if (userDisplay.onUpdate) {
      userDisplay.onUpdate(usedDisplayConfig);
      return;
    }

    if (userDisplay.localStorageKey) {
      // update the local storage only if a cached config key has been provided
      if (usedDisplayConfig) {
        // save it to local storage
        localStorage.setItem(
          userDisplay.localStorageKey,
          JSON.stringify(usedDisplayConfig)
        );
      } else {
        // delete the cached display config
        localStorage.removeItem(userDisplay.localStorageKey);
      }
    }

    this.regenerateCompletedDisplayConfig(this.props, usedDisplayConfig);
  };

  // Regenerates the config to be used for the current display.
  // This updates the table config wth the user display config
  regenerateCompletedDisplayConfig = (
    props,
    newUserDisplayConfig,
    forceUpdate = false
  ) => {
    const { config, data, update } = props;
    const displayConfig = getCompletedDisplayConfig(
      config,
      newUserDisplayConfig
    );

    // update the data to display
    // if necessary (ie either sorting or filtering has changed)
    const { displayConfig: currentDisplayConfig } = this.state;
    let { dataToDisplay } = this.state;

    if (
      forceUpdate ||
      !isequal(currentDisplayConfig.sortSetup, displayConfig.sortSetup) ||
      !isequal(currentDisplayConfig.applyFilters, displayConfig.applyFilters) ||
      !isequal(currentDisplayConfig.filters, displayConfig.filters) ||
      !isequal(
        currentDisplayConfig.columns.map(col => col.filter),
        displayConfig.columns.map(col => col.filter)
      ) ||
      !isequal(
        currentDisplayConfig.fixedColumns.map(col => col.filter),
        displayConfig.fixedColumns.map(col => col.filter)
      )
    ) {
      dataToDisplay = update?.disableClientSideSortFilter
        ? data
        : getDataToDisplay(data, displayConfig);
    }

    // update the state
    this.setState(
      {
        userDisplayConfig: newUserDisplayConfig,
        displayConfig,
        dataToDisplay
      },
      () => {
        this.updateGrid();
      }
    );
  };

  toggleEdition = () => {
    this.setState(prevState => ({
      isEditing: !prevState.isEditing
    }));
  };

  reorderColumns = result => {
    // reorder user columns following drag n drop in ColHeadersEditor

    // note: col headers drag n dropping may result in the col headers editor being scrolled too far
    // (because of the vertical scrollbar width buffer)
    // so we resync col headers scroll here to where the grid outer ref has stopped
    if (
      this.floatingColHeadersRef.current &&
      this.floatingGridOuterRef.current
    ) {
      this.floatingColHeadersRef.current.scrollLeft =
        this.floatingGridOuterRef.current.scrollLeft;
    }

    if (
      !result.destination ||
      result.source.index === result.destination.index
    ) {
      // dropped outside the list or left at the same place
      return;
    }

    const newUserDisplayConfig = this.getCurrentDisplayConfig();
    const {
      displayConfig: { columns }
    } = this.state;
    const currentColumnKeys = columns.map(col => col.key);

    newUserDisplayConfig.columns = reorder(
      currentColumnKeys,
      result.source.index,
      result.destination.index
    );

    this.updateUserDisplayConfig(newUserDisplayConfig);
  };

  setColumnWidth = (colKey, width) => {
    // set a user column width from resizing event in ColHeadersEditor
    const newUserDisplayConfig = this.getCurrentDisplayConfig();

    if (!newUserDisplayConfig.widths) {
      newUserDisplayConfig.widths = {};
    }
    newUserDisplayConfig.widths[colKey] = width;

    this.updateUserDisplayConfig(newUserDisplayConfig);
  };

  setRowsHeight = height => {
    // set a user rows height from resizing event in ColHeadersEditor
    const newUserDisplayConfig = this.getCurrentDisplayConfig();

    newUserDisplayConfig.rowsHeight = height;

    this.updateUserDisplayConfig(newUserDisplayConfig);
  };

  setSortSetup = (setup, sortConfig) => {
    const cfg = this.getCurrentDisplayConfig();

    if (setup) {
      cfg.sortSetup = {
        ...setup,
        config: sortConfig
      };
    } else {
      cfg.sortSetup = undefined;
    }
    this.updateUserDisplayConfig(cfg);
  };

  setGroupBy = group => {
    const newUserDisplayConfig = this.getCurrentDisplayConfig();
    newUserDisplayConfig.groupBy = group;
    this.updateUserDisplayConfig(newUserDisplayConfig);
  };

  toggleFilters = () => {
    const newUserDisplayConfig = this.getCurrentDisplayConfig();

    newUserDisplayConfig.applyFilters = !newUserDisplayConfig.applyFilters;

    this.updateUserDisplayConfig(newUserDisplayConfig);
  };

  setColumnFilter = (colKey, filterValue, filterConfig) => {
    // set a user filter on a specific column
    const newUserDisplayConfig = this.getCurrentDisplayConfig();

    if (!newUserDisplayConfig.filters) {
      newUserDisplayConfig.filters = {};
    }

    if (filterValue === undefined) {
      delete newUserDisplayConfig.filters[colKey];
    } else {
      newUserDisplayConfig.filters[colKey] = {
        value: filterValue,
        config: filterConfig
      };
    }
    this.updateUserDisplayConfig(newUserDisplayConfig);
  };

  toggleColumnSelection = colKey => {
    const newUserDisplayConfig = this.getCurrentDisplayConfig();

    const {
      displayConfig: { columns }
    } = this.state;
    const currentColumnKeys = columns.map(col => col.key);
    const colIndex = currentColumnKeys.findIndex(key => key === colKey);
    if (colIndex > -1) {
      // remove the column
      currentColumnKeys.splice(colIndex, 1);
      // remove other display configs related to it
      if (newUserDisplayConfig.filters?.[colKey]) {
        delete newUserDisplayConfig.filters[colKey];
      }
      if (newUserDisplayConfig.widths?.[colKey]) {
        delete newUserDisplayConfig.widths[colKey];
      }
      if (newUserDisplayConfig.sortSetup?.key === colKey) {
        delete newUserDisplayConfig.sortSetup;
      }
      if (newUserDisplayConfig.groupBy?.column === colKey) {
        delete newUserDisplayConfig.groupBy;
      }
    } else {
      // add it
      currentColumnKeys.push(colKey);
    }
    newUserDisplayConfig.columns = currentColumnKeys;
    this.updateUserDisplayConfig(newUserDisplayConfig);
  };

  resetDisplayConfig = () => {
    this.updateUserDisplayConfig(null);
  };

  /** @type {import('./table.type').TableContext['toggleRowSelected']} */
  toggleRowSelected = (rowSelector, options = {}) => {
    const next = computeNextSelection(
      rowSelector,
      options,
      this.state,
      this.props.config || {}
    );

    this.setState(next);
    this.props.update?.setSelectedRows?.(next.selectedData);
  };

  toggleSelectAll = () => {
    const { dataToDisplay, selectedData } = this.state;
    const { update } = this.props;

    const newSelectedData =
      selectedData.length > 0
        ? []
        : dataToDisplay.map(rowData =>
            getByPath(rowData, this.dataSelectorPath)
          );

    this.setState({
      selectedData: newSelectedData,
      lastSelectedRowIndex: null
    });
    // if callback provided
    // warn the client (parent) about the changes in selectedRows
    if (update.setSelectedRows) {
      update.setSelectedRows(newSelectedData);
    }
  };

  toggleGroup = label => {
    // Update display config to save the uncollapsed group
    const newDisplayConfig = this.getCurrentDisplayConfig();
    newDisplayConfig.groupBy = {
      ...newDisplayConfig.groupBy,
      // If the selected group row is already open, collapse it
      selected: newDisplayConfig.groupBy.selected === label ? undefined : label
    };
    this.updateUserDisplayConfig(newDisplayConfig);
  };

  toggleDisplayDiffs = () => {
    this.setState(
      prevState => ({
        displayDiffs: !prevState.displayDiffs
      }),
      () => {
        this.updateGrid();
      }
    );
  };

  toggleShowAggregation = () => {
    this.setState(
      prevState => ({ showAggregation: !prevState.showAggregation }),
      () => {
        this.updateGrid();
        // when displaying the aggregation row
        // check if the grid is already scrolled horizontally
        // and potentially apply scroll to the row and box shadow
        if (
          this.state.showAggregation &&
          !!this.floatingGridOuterRef.current?.scrollLeft
        ) {
          // apply box shadow
          if (this.fixedAggregationRowRef.current) {
            this.fixedAggregationRowRef.current.style.setProperty(
              CSS_BOX_SHADOW,
              FIXED_GRID_VERTICAL_SHADOW
            );
          }
          // scroll the floating aggregation row left
          if (this.floatingAggregationRowRef.current) {
            this.floatingAggregationRowRef.current.scrollLeft =
              this.floatingGridOuterRef.current.scrollLeft;
          }
        }
      }
    );
  };

  clickExport = () => {
    const { update } = this.props;
    // if getConfirmationSetup is provided,
    // it means the client is expecting a confirmation modal to be thrown before
    const getConfirmationSetup = update?.exportSetup?.getConfirmationSetup;
    if (getConfirmationSetup) {
      this.setState({
        exportConfirmation: getConfirmationSetup()
      });
      return;
    }

    // otherwise call directly the exportData function
    this.exportData();
  };

  cancelExport = () => {
    this.setState({
      exportConfirmation: null
    });
  };

  deleteSelectedRows = () => {
    const { update } = this.props;

    if (!(update?.setSelectedRows && update?.edit?.onDeleteSelectedRows))
      return;
    const { selectedData } = this.state;

    this.setState({
      selectedData: [],
      lastSelectedRowIndex: null
    });
    update.setSelectedRows([]);
    update.edit.onDeleteSelectedRows(selectedData);
  };

  onClickCompareSelectedRows = () => {
    const { selectedData } = this.state;

    if (selectedData.length !== 2) {
      notify.error('Please select 2 rows to compare');
      console.error(
        'Could not compare selected rows: not enough rows selected',
        selectedData
      );
      Sentry.captureException(
        new Error('Could not compare selected rows: not enough rows selected'),
        {
          extra: {
            selectedData
          }
        }
      );
      return;
    }

    this.setState({
      showTracesDiffViewer: true
    });
  };

  exportData = async () => {
    const { dataToDisplay, displayConfig, fileType } = this.state;
    const { config, workflowContext, update } = this.props;

    const { edit } = update || {};
    const { dataSelectorPath = 'rowId' } = config;
    const { modifiedData } = edit || {};

    const dataForExport = modifiedData
      ? dataToDisplay.map(row => {
          const rowId = getByPath(row, dataSelectorPath);
          if (rowId !== undefined && modifiedData[rowId]) {
            return { ...row, ...modifiedData[rowId] };
          }
          return row;
        })
      : dataToDisplay;

    // build a function that can apply a single row parsing to all data
    // if an export function from the client exists use it (it will implement asynchronous pagination)
    // otherwise just pass a function that applies the row mapping to all currently available data
    const generateExportPayload =
      update?.exportSetup?.generateExportPayload ||
      (rowPayloadFn => dataForExport.map(rowPayloadFn));

    // update state to exporting
    this.setState({
      exporting: true,
      exportConfirmation: null
    });

    await exportDataTable(
      generateExportPayload,
      displayConfig,
      {
        ...DEFAULT_EXPORT_CSV,
        ...config.exportCsv
      },
      workflowContext,
      fileType
    );

    // unset exporting state
    this.setState({
      exporting: false
    });
  };

  // Change type file for export function
  changeTypeFile = type => {
    this.setState({ fileType: type });
  };

  // Table Headers
  renderTableHeaders = (nbRows, shrinkRightIcons, currentNbRows) => {
    const { config, update, headerConfig } = this.props;
    const {
      userDisplayConfig,
      displayConfig,
      selectedData,
      isEditing,
      displayDiffs,
      showAggregation
    } = this.state;

    const isUserConfigModified =
      !!userDisplayConfig && Object.keys(userDisplayConfig).length > 0;

    return (
      <TableHeaders
        changeTypeFile={this.changeTypeFile}
        currentNbRows={currentNbRows}
        displayConfig={displayConfig}
        displayDiffs={displayDiffs}
        fileType={this.state.fileType}
        headerConfig={headerConfig}
        isEditing={isEditing}
        nbRows={nbRows}
        nbRowsSelected={selectedData.length}
        onClickCompareSelectedRows={this.onClickCompareSelectedRows}
        onDeleteSelectedRows={
          update?.edit?.onDeleteSelectedRows
            ? this.deleteSelectedRows
            : undefined
        }
        onExport={this.clickExport}
        resetDisplayConfig={
          isUserConfigModified ? this.resetDisplayConfig : null
        }
        saveConfig={this.saveConfig}
        setGroupBy={this.setGroupBy}
        showAggregation={showAggregation}
        shrinkRightIcons={shrinkRightIcons}
        tableConfig={config}
        toggleColumnSelection={this.toggleColumnSelection}
        toggleDisplayDiffs={this.toggleDisplayDiffs}
        toggleEdition={this.toggleEdition}
        toggleFilters={this.toggleFilters}
        toggleSelectAll={this.toggleSelectAll}
        toggleShowAggregation={this.toggleShowAggregation}
        update={update}
        updateUserDisplayConfig={this.updateUserDisplayConfig}
      />
    );
  };

  // Table Column  Headers and Filters
  renderTableColumnHeaders = (
    selectBoxWidth,
    fixedColumns,
    floatingColumns,
    sortSetup,
    applyFilters,
    invalidFilters,
    isEditing,
    mergeGrids
  ) => {
    const { disableClientSideSortFilter } = this.props.update || {};

    // fixed column headers container
    const fixedColumnHeaders = (
      <div
        className="z-1 flex flex-col transition-[box-shadow] duration-200 ease-in-out"
        ref={this.fixedColHeadersRef}
      >
        <ColHeaders
          columns={mergeGrids ? [] : fixedColumns}
          disableClientSideSortFilter={disableClientSideSortFilter}
          height={30}
          selectBoxWidth={selectBoxWidth}
          setSortSetup={this.setSortSetup}
          sortSetup={sortSetup}
        />
        <AnimatePresence>
          {applyFilters && (
            <TableFilters
              columns={mergeGrids ? [] : fixedColumns}
              disableClientSideSortFilter={disableClientSideSortFilter}
              invalidFilters={invalidFilters}
              selectBoxWidth={selectBoxWidth}
              setColumnFilter={this.setColumnFilter}
            />
          )}
        </AnimatePresence>
      </div>
    );

    // floating column headers container, scrollable in sync with the floating columns grid
    const floatingColumnHeaders = (
      <div
        className="flex flex-row flex-nowrap overflow-x-hidden"
        onScroll={this.onFloatingColHeadersScroll}
        ref={this.floatingColHeadersRef}
      >
        {mergeGrids && (
          <div className="flex flex-col">
            <ColHeaders
              columns={fixedColumns}
              disableClientSideSortFilter={disableClientSideSortFilter}
              height={30}
              setSortSetup={this.setSortSetup}
              sortSetup={sortSetup}
            />
            <AnimatePresence>
              {applyFilters && (
                <TableFilters
                  columns={fixedColumns}
                  disableClientSideSortFilter={disableClientSideSortFilter}
                  invalidFilters={invalidFilters}
                  setColumnFilter={this.setColumnFilter}
                />
              )}
            </AnimatePresence>
          </div>
        )}
        <div className="flex flex-col">
          {isEditing ? (
            <ColHeadersEditor
              columns={floatingColumns}
              height={30}
              reorderColumns={this.reorderColumns}
              setColumnWidth={this.setColumnWidth}
            />
          ) : (
            <ColHeaders
              columns={floatingColumns}
              disableClientSideSortFilter={disableClientSideSortFilter}
              height={30}
              setSortSetup={this.setSortSetup}
              sortSetup={sortSetup}
            />
          )}
          <AnimatePresence>
            {applyFilters && (
              <TableFilters
                columns={floatingColumns}
                disableClientSideSortFilter={disableClientSideSortFilter}
                invalidFilters={invalidFilters}
                setColumnFilter={this.setColumnFilter}
              />
            )}
          </AnimatePresence>
        </div>
        <div className="shrink-0" style={{ width: SCROLLBAR_SIZE }} />
      </div>
    );

    // aggregate the 2 sets of column headers
    return (
      <div
        className="z-2 flex flex-row flex-nowrap transition-[box-shadow] duration-200 ease-in-out"
        ref={this.colHeadersRef}
      >
        {fixedColumnHeaders}
        {floatingColumnHeaders}
      </div>
    );
  };

  // Table Grids
  renderTableGrids = (
    height,
    fixedGridWidth,
    fixedColumns,
    floatingGridWidth,
    floatingColumns,
    sharedGridContext,
    mergeGrids,
    rowCount,
    overscanRowCount,
    loading
  ) => {
    // Note: Grid takes as child a component that will be used to render all the cells by react-window
    // react-window props are made of rowIndex, columnIndex and INLINE POSITIONING STYLE
    // so we will use a rendering component TableCell that has access to the table context

    const rowsHeight = this.getRowHeight();

    const usedFixedColumns = mergeGrids ? [] : fixedColumns;
    const usedFloatingColumns = mergeGrids
      ? [...fixedColumns, ...floatingColumns]
      : floatingColumns;

    // fixed columns grid

    // if the floating grid is effectively scrolling horizontally we need to add a blank of height SCROLLBAR_SIZE
    // below the fixed grid to compensate for the one injected by react-window at the bottom of the floating grid
    // or the 2 grids will be displaced when reaching the bottom of the container
    // small subtlety: if the grid is also scrollable vertically
    // we need to take into account the width of the vertical scrollbar to check if the grid will be scrollable horizontally...
    const floatingGridHorizontalScroll =
      usedFloatingColumns.reduce((sum, col) => sum + col.width, 0) >
      floatingGridWidth - (rowsHeight * rowCount > height ? SCROLLBAR_SIZE : 0);

    const fixedGridHeight =
      height - (floatingGridHorizontalScroll ? SCROLLBAR_SIZE : 0);
    const fixedGridContext = {
      ...sharedGridContext,
      isFixedGrid: true,
      columns: usedFixedColumns,
      allColumns: [...usedFixedColumns, ...usedFloatingColumns]
    };
    const fixedGrid = (
      <TableContext.Provider value={fixedGridContext}>
        <Grid
          className={cn(
            'z-1 overflow-x-hidden transition-[box-shadow] duration-200 ease-in-out',
            loading && 'opacity-30'
          )}
          columnCount={usedFixedColumns.length + 1}
          columnWidth={this.getFixedColumnWidth}
          estimatedRowHeight={rowsHeight}
          height={fixedGridHeight}
          onScroll={this.onFixedGridScroll}
          outerRef={this.fixedGridOuterRef}
          overscanRowCount={overscanRowCount || DEFAULT_OVERSCANROWCOUNT}
          ref={this.fixedGridRef}
          rowCount={rowCount}
          rowHeight={this.getRowHeight}
          style={{
            scrollbarWidth: 'none'
          }}
          width={fixedGridWidth}
        >
          {TableCell}
        </Grid>
      </TableContext.Provider>
    );

    // floating columns grid
    const floatingGridContext = {
      ...sharedGridContext,
      isFixedGrid: false,
      columns: usedFloatingColumns
    };
    const floatingGrid = (
      <TableContext.Provider value={floatingGridContext}>
        <ToggleAwareGridDriver
          gridRef={this.floatingGridRef}
          toggle={mergeGrids}
        >
          <Grid
            className={cn(loading && 'opacity-30')}
            columnCount={usedFloatingColumns.length}
            columnWidth={
              mergeGrids
                ? this.getMergedColumnsWidth
                : this.getFloatingColumnWidth
            }
            estimatedColumnWidth={
              mergeGrids
                ? this.getEstimatedMergedColumnWidth()
                : this.getEstimatedFloatingColumnWidth()
            }
            estimatedRowHeight={rowsHeight}
            height={height}
            onScroll={this.onFloatingGridScroll}
            outerRef={this.floatingGridOuterRef}
            overscanRowCount={overscanRowCount || DEFAULT_OVERSCANROWCOUNT}
            ref={this.floatingGridRef}
            rowCount={rowCount}
            rowHeight={this.getRowHeight}
            width={floatingGridWidth}
          >
            {TableCell}
          </Grid>
        </ToggleAwareGridDriver>
      </TableContext.Provider>
    );

    // aggregate the 2 grids
    return (
      <div className="flex flex-row flex-nowrap">
        {fixedGrid}
        {floatingGrid}
      </div>
    );
  };

  renderRowsDiffViewer = () => {
    const { dataToDisplay } = this.state;

    const selectedRowIdBefore = this.state.selectedData?.[0];
    const selectedRowIdAfter = this.state.selectedData?.[1];

    const selectedRowsDataBefore = dataToDisplay.filter(
      d => getByPath(d, this.dataSelectorPath) === selectedRowIdBefore
    );

    const selectedRowsDataAfter = dataToDisplay.filter(
      d => getByPath(d, this.dataSelectorPath) === selectedRowIdAfter
    );

    return (
      <TableRowDiffViewer
        after={selectedRowsDataAfter[0]}
        before={selectedRowsDataBefore[0]}
        onOpenChange={open => this.setState({ showTracesDiffViewer: open })}
        open={this.state.showTracesDiffViewer}
      />
    );
  };

  // Table Aggregation row
  renderTableAggregationRow = (
    selectBoxWidth,
    fixedColumns,
    floatingColumns,
    mergeGrids,
    data
  ) => {
    // fixed column aggregation row container
    const fixedColumnAggregationRow = (
      <div
        className="transition-[box-shadow] duration-200 ease-in-out"
        ref={this.fixedAggregationRowRef}
      >
        <AggregationRow
          columns={mergeGrids ? [] : fixedColumns}
          data={data}
          height={30}
          selectBoxWidth={selectBoxWidth}
        />
      </div>
    );

    // floating column aggregation row container, scrollable in sync with the floating columns grid
    const floatingColumnAggregationRow = (
      <div
        className="flex flex-row flex-nowrap overflow-x-hidden"
        ref={this.floatingAggregationRowRef}
      >
        {mergeGrids && (
          <AggregationRow columns={fixedColumns} data={data} height={30} />
        )}
        <AggregationRow columns={floatingColumns} data={data} height={30} />
        <div className="shrink-0" style={{ width: SCROLLBAR_SIZE }} />
      </div>
    );

    // aggregate the 2 sets of aggregation cells
    return (
      <div className="flex flex-row flex-nowrap bg-accent">
        {fixedColumnAggregationRow}
        {floatingColumnAggregationRow}
      </div>
    );
  };

  render = () => {
    const { config, update } = this.props;

    const { dataSelectorPath = 'rowId', overscanRowCount } = config;
    const { onClickUpdate, edit, pagination, group, loading } = update;

    const {
      dataToDisplay,
      displayConfig,
      selectedData,
      isEditing,
      displayDiffs,
      showAggregation,
      exporting,
      exportConfirmation
    } = this.state;

    const {
      selectBoxWidth,
      fixedColumns,
      columns: floatingColumns,
      rowsHeight,
      minRowsHeight,
      sortSetup,
      groupBy,
      applyFilters,
      rowInlineStyle
    } = displayConfig;

    // check filtered columns and report if invalid filters combination
    const filteredFixedColumns = fixedColumns.filter(col => col.filter);
    const filteredFloatingColumns = floatingColumns.filter(col => col.filter);
    const nbActiveFilters =
      filteredFixedColumns.length + filteredFloatingColumns.length;
    const invalidFilters =
      applyFilters && dataToDisplay.length === 0 && nbActiveFilters > 0;

    // table context that will be passed down to the linked cell renderers
    const tableData = loading ? [] : dataToDisplay;
    const displayGroups = tableData.length > 0 && group && groupBy;

    // row count depends on if a group is uncollapsed
    // AND if there is pagination
    let rowCount;
    if (displayGroups && groupBy.selected !== undefined) {
      const groupData = dataToDisplay.find(
        data => deepGet(data, group.indexPath) === groupBy.selected
      );
      const groupRows = getByPath(groupData, group.rowsPath);
      if (groupRows) {
        rowCount = dataToDisplay.length + groupRows.length - 1;
      } else {
        rowCount = dataToDisplay.length;
      }
    } else {
      rowCount = tableData.length; // todo: check totalCount if pagination
    }
    // Get the number of rows from the workflow context (in case of the table displays the list of a workflow's traces)
    const currentNbRows = this.props.workflowContext?.totalRowCount;

    const rowLoadingState =
      this.props.workflowContext?.totalRowCount === 'loading';

    // Use NB_ROWS_IF_EMPTY only when there's actually no data
    const actualRowCount = currentNbRows || rowCount;
    const displayRowCount =
      actualRowCount > 0 ? actualRowCount : NB_ROWS_IF_EMPTY;
    rowCount =
      actualRowCount > NB_ROWS_IF_EMPTY ? actualRowCount : NB_ROWS_IF_EMPTY;

    const displayLoadingState = loading || exporting;

    // context shared between fixed and floating grids
    const sharedGridContext = {
      loading: displayLoadingState,
      data: tableData,
      isEditing,
      selectedData,
      allowRowsSelection:
        !!onClickUpdate ||
        (edit && !!edit.onClickBatchEdit) ||
        !!update?.initialSelectedRows ||
        selectedData.length > 0,
      dataSelectorPath,
      selectionReadOnly: update?.selectionReadOnly,
      toggleRowSelected: this.toggleRowSelected,
      pagination,
      group: displayGroups
        ? {
            ...group,
            display: groupBy,
            toggle: this.toggleGroup
          }
        : null,
      edit,
      displayDiffs,
      rowInlineStyle,
      errorSchema: this.props.errorSchema
    };

    return (
      <>
        <div
          className={cn(
            'size-full grow overflow-hidden rounded-md border border-border bg-card text-foreground',
            this.props.className
          )}
          data-cy="table-container"
        >
          <AutoSizer>
            {({ width, height }) => {
              // Check width split between fixed and floating grids
              let fixedGridWidth =
                selectBoxWidth +
                fixedColumns.reduce((sum, col) => sum + col.width, 0);
              let floatingGridWidth = width - fixedGridWidth;

              // if there is not enough room on the right to let the floating grid scroll horizontally
              // merge fixed and floating grid, leaving only select boxes as fixed

              // merge if less than FLOATING_GRID_MIN_RATIO of the total grid size is available for the floating grid
              const mergeGrids =
                floatingColumns.length > 0 &&
                floatingGridWidth < width * FLOATING_GRID_MIN_RATIO;
              if (mergeGrids) {
                fixedGridWidth = selectBoxWidth;
                floatingGridWidth = width - fixedGridWidth;
              }

              // shrink table headers right icons if the bale is too small
              const shrinkRightIcons = width < SHRINK_HEADER_ICON_THRESHOLD;

              return (
                <div className="flex flex-col" style={{ width, height }}>
                  {/* Table Headers */}
                  {this.renderTableHeaders(
                    rowLoadingState ? 'loading' : displayRowCount,
                    shrinkRightIcons,
                    currentNbRows
                  )}
                  {/* Columns Headers */}
                  {this.renderTableColumnHeaders(
                    selectBoxWidth,
                    fixedColumns,
                    floatingColumns,
                    sortSetup,
                    applyFilters,
                    invalidFilters,
                    isEditing,
                    mergeGrids
                  )}
                  {/* Grids */}
                  <div className="relative m-0 grow whitespace-nowrap p-0 text-[13px] text-card-foreground">
                    {isEditing && (
                      <ResizableRow
                        height={rowsHeight}
                        minHeight={minRowsHeight}
                        setRowsHeight={this.setRowsHeight}
                      />
                    )}
                    <AutoSizer disableWidth>
                      {({ height: gridsHeight }) =>
                        this.renderTableGrids(
                          gridsHeight,
                          fixedGridWidth,
                          fixedColumns,
                          floatingGridWidth,
                          floatingColumns,
                          sharedGridContext,
                          mergeGrids,
                          rowCount,
                          overscanRowCount,
                          displayLoadingState,
                          showAggregation
                        )
                      }
                    </AutoSizer>
                    {displayLoadingState && (
                      <div className="absolute top-0 z-10 flex size-full flex-row flex-nowrap items-center justify-center opacity-100">
                        <TraceIconSpinner className="m-0" small />
                      </div>
                    )}
                    {invalidFilters && (
                      <div className="flex flex-col flex-nowrap items-center">
                        <div className="absolute top-5 z-20 w-fit rounded-sm bg-destructive px-4 py-3 text-destructive-foreground">
                          {nbActiveFilters === 1 ? (
                            <>
                              No data to display,
                              <span className="mx-1 font-bold">1 filter</span>
                              is active
                            </>
                          ) : (
                            <>
                              No data to display,
                              <span className="mx-1 font-bold">
                                {`${nbActiveFilters} filters`}
                              </span>
                              are active
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Aggregation Row */}
                  {showAggregation &&
                    this.renderTableAggregationRow(
                      selectBoxWidth,
                      fixedColumns,
                      floatingColumns,
                      mergeGrids,
                      tableData
                    )}
                </div>
              );
            }}
          </AutoSizer>
          {exportConfirmation && (
            <ConfirmationModal
              {...exportConfirmation}
              cancel={this.cancelExport}
              confirm={this.exportData}
            />
          )}
        </div>
        {this.renderRowsDiffViewer()}
      </>
    );
  };
}

export default compose(withWorkflowContext, withWorkflowOverviewContext)(Table);
